import { useEffect, useRef, useState } from "react";
import { portalTourSteps } from "./portal-tour";
import type { AccountTab } from "./portal-navigation";

export function PortalTour({
  admin,
  accounts,
  currentAccount,
  onNavigate,
  onClose,
}: {
  admin: boolean;
  accounts: { id: string; name: string; account_number: string }[];
  currentAccount: string | null;
  onNavigate: (
    section: string,
    accountId?: string,
    tab?: AccountTab,
  ) => Promise<void>;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [account, setAccount] = useState(
    currentAccount ?? (accounts.length === 1 ? accounts[0]!.id : ""),
  );
  const [busy, setBusy] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const steps = portalTourSteps(admin);
  const step = steps[index]!;
  const needsAccount = step.section === "account";
  const selected = accounts.some((row) => row.id === account) ? account : "";
  useEffect(() => {
    heading.current?.focus();
  }, [index]);
  return (
    <section className="cr-tour" aria-label="Portal tour">
      <div className="cr-tour-heading">
        <p className="cr-kicker">
          Portal tour · {index + 1} of {steps.length}
        </p>
        <button className="cr-text-button" onClick={onClose}>
          Close tour
        </button>
      </div>
      <h2 ref={heading} tabIndex={-1}>
        {step.title}
      </h2>
      <p>{step.text}</p>
      <ul>
        {step.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
      {needsAccount &&
        (accounts.length ? (
          <label className="cr-field">
            <span>{admin ? "Customer for this step" : "Your account"}</span>
            <select
              value={selected}
              onChange={(event) => setAccount(event.target.value)}
            >
              <option value="" disabled>
                Choose an account
              </option>
              {accounts.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name} · {row.account_number}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p>
            No account is available for this step.{" "}
            {admin
              ? "Add a customer from the first step when you are ready."
              : "Ask Cache River to check your account access."}
          </p>
        ))}
      <div className="cr-tour-actions">
        <button
          className="cr-button"
          disabled={busy || (needsAccount && !selected)}
          onClick={async () => {
            setBusy(true);
            try {
              await onNavigate(
                step.section,
                needsAccount ? selected : undefined,
                step.tab,
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Opening…" : step.action}
        </button>
        <div>
          <button
            className="cr-button secondary"
            disabled={index === 0 || busy}
            onClick={() => setIndex((value) => value - 1)}
          >
            Previous
          </button>
          {index < steps.length - 1 ? (
            <button
              className="cr-button secondary"
              disabled={busy}
              onClick={() => setIndex((value) => value + 1)}
            >
              Next
            </button>
          ) : (
            <button className="cr-button secondary" onClick={onClose}>
              Finish tour
            </button>
          )}
        </div>
      </div>
      <p className="cr-form-note">
        The tour opens pages without submitting forms or changing service.
      </p>
    </section>
  );
}
